/**
 * TOSS Web Application - Router Layouts
 *
 * Suspense wrapper, layout and error-boundary components for the router.
 * Moved verbatim out of router/index.tsx when the route table was split
 * into per-domain modules under router/routes/ (no behavior change).
 */

import { Suspense, ReactNode, useEffect } from 'react';
import { Navigate, Outlet, useRouteError } from 'react-router-dom';
import { ROUTES, getDefaultRoute } from '@/constants';
import { useAuthStore } from '@/store';
import { AppLayout, ClientLayout } from '@/components/layout';
import { PageLoading, ErrorFallbackScreen } from '@/components/ui';
import { ProtectedRoute } from './ProtectedRoute';
import { GuestRoute } from './GuestRoute';
import { ClientRoute } from './ClientRoute';

// ============================================================================
// Suspense Wrapper
// ============================================================================

/**
 * Suspense wrapper with loading fallback
 */
function SuspenseWrapper({ children }: { children: ReactNode }): JSX.Element {
  return <Suspense fallback={<PageLoading />}>{children}</Suspense>;
}

/**
 * Lazy page component wrapper
 */
function LazyPage({ children }: { children: ReactNode }): JSX.Element {
  return <SuspenseWrapper>{children}</SuspenseWrapper>;
}

// ============================================================================
// Home Redirect (role-aware)
// ============================================================================

function HomeRedirect(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const target = user ? getDefaultRoute(user.role) : ROUTES.applications.list;
  return <Navigate to={target} replace />;
}

// ============================================================================
// Layout Components
// ============================================================================

/**
 * Root layout for protected routes
 * Includes authentication check and app layout
 *
 * Renders `AppLayout` — the INTERNAL shell — above its own guard, so the audience check
 * cannot live in here: by the time `ProtectedRoute` decides anything, the sidebar, the
 * header's `/api/lk/*` polling and `useMessengerSignalR()` have already mounted. The
 * route table therefore wraps this whole layout in `InternalOnlyRoute` (router/index.tsx),
 * exactly as it does for `MessengerLayout`. `HomeRedirect` is mounted OUTSIDE this layout
 * for the same reason — it must stay reachable by a client session so it can send it to
 * `/client`.
 */
function ProtectedLayout(): JSX.Element {
  return (
    <ProtectedRoute>
      <AppLayout>
        <SuspenseWrapper>
          <Outlet />
        </SuspenseWrapper>
      </AppLayout>
    </ProtectedRoute>
  );
}

/**
 * Layout for messenger routes — viewport-locked, no padding
 */
function MessengerLayout(): JSX.Element {
  return (
    <ProtectedRoute>
      <AppLayout fullHeight>
        <SuspenseWrapper>
          <Outlet />
        </SuspenseWrapper>
      </AppLayout>
    </ProtectedRoute>
  );
}

/**
 * Layout for the authenticated client portal (`/client/*`).
 *
 * Deliberately NOT `ProtectedLayout` with a different guard: `AppLayout` is the
 * internal shell (sidebar, command palette, calculator panel, messenger FAB, a global
 * `useMessengerSignalR()` subscription and `/api/lk/*` unread polling in its header) —
 * none of which a customer may see or is allowed to call. `ClientLayout` is a separate
 * chrome with the portal's own navigation pattern: sidebar on desktop, bottom
 * navigation on the phone.
 *
 * The guard is role-based (`ClientRoute`, decision Р31) because a client account never
 * enters the permission matrix, so any key-based guard would deny it by construction.
 */
function ClientProtectedLayout(): JSX.Element {
  return (
    <ClientRoute>
      <ClientLayout>
        <SuspenseWrapper>
          <Outlet />
        </SuspenseWrapper>
      </ClientLayout>
    </ClientRoute>
  );
}

/**
 * Layout for the portal's anonymous screens (client login / password recovery).
 *
 * Same shape as `PublicLayout` — `GuestRoute` bounces an already-signed-in visitor to
 * their default route, which for a client session is `/client` (see `getDefaultRoute`).
 */
function ClientPublicLayout(): JSX.Element {
  return (
    <GuestRoute>
      <SuspenseWrapper>
        <Outlet />
      </SuspenseWrapper>
    </GuestRoute>
  );
}

/**
 * Layout for public auth routes (login, register, etc.)
 * Does not include app layout.
 * Uses GuestRoute to redirect authenticated users away from auth pages.
 */
function PublicLayout(): JSX.Element {
  return (
    <GuestRoute>
      <SuspenseWrapper>
        <Outlet />
      </SuspenseWrapper>
    </GuestRoute>
  );
}

// ============================================================================
// Route-level Error Boundary
// ============================================================================

/**
 * errorElement for the data router. Render errors inside routes are caught by
 * React Router BEFORE they can bubble to the app-level <ErrorBoundary>;
 * without this element users would see the router's built-in English error
 * screen with a raw stack trace.
 */
function RouteErrorBoundary(): JSX.Element {
  const error = useRouteError();

  useEffect(() => {
    console.error('[RouteErrorBoundary]', error);
  }, [error]);

  return <ErrorFallbackScreen error={error} />;
}

export {
  SuspenseWrapper,
  LazyPage,
  HomeRedirect,
  ProtectedLayout,
  MessengerLayout,
  PublicLayout,
  ClientProtectedLayout,
  ClientPublicLayout,
  RouteErrorBoundary,
};
